import type { BlueCalloutComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

const PANEL_BG = "#00B862";

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/** Blue callout renderer with text, image and optional link. */
export function BlueCalloutRenderer({ component }: { component: BlueCalloutComponent }) {
  const { title, text, imageSrc, imageAlt, ctaHref } = component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const resolvedHref = resolveLinkHref(ctaHref);

  const panelContent = (
    <>
      <h3 className="text-[17px] xl:text-[19px] md:text-base font-bold text-white">{title}</h3>
      <p className="mt-2 text-base xl:text-lg leading-6 text-white/95">{text}</p>
      <div className="mt-4 flex items-center text-white">
        <ChevronRightIcon className="h-5 w-5" />
      </div>
    </>
  );

  // Image container: on mobile limited height (min 50vmin / 280px); on desktop stretches to match content panel
  const imageContainerStyle: React.CSSProperties = { minHeight: "min(50vmin, 280px)" };

  return (
    <div className="grid gap-0 md:grid-cols-2 md:items-stretch overflow-hidden rounded-lg ring-1 ring-black/10">
      <div
        className="flex flex-col items-start justify-center rounded-t-lg p-6 md:rounded-none md:rounded-l-lg text-left"
        style={{ backgroundColor: PANEL_BG }}
      >
        {resolvedHref ? (
          <RuntimeLink href={resolvedHref} className="group block w-full text-left">
            {panelContent}
          </RuntimeLink>
        ) : (
          panelContent
        )}
      </div>
      <div
        className={mergeClassNames("relative min-h-0 overflow-hidden rounded-b-lg md:rounded-none md:rounded-r-lg", overrideProps.className)}
        style={mergeStyles(imageContainerStyle, overrideProps.style)}
      >
        <RuntimeImage
          src={src}
          alt={imageAlt}
          className="absolute inset-0 h-full w-full object-cover"
          style={objectPositionStyle}
          priority
        />
      </div>
    </div>
  );
}
